const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const copy = {
  moduleName: 'Quit Smoking',
  /** Short, so the bottom bar label fits on one line. */
  navLabel: 'Quit',
  loading: 'Loading…',
  logging: 'Logging one cigarette…',
  invalidDay: "That day can't be edited. Pick a day up to today.",
  stepOf: (n: number, total: number) => `Step ${n} of ${total}`,
  leftToday: (n: number) => `${n} left today`,
  atLimit: 'At your limit for today',
  overBy: (n: number) => `${n} over your limit today`,
  smokeFreeToday: 'Smoke-free today',
  progress: 'Your taper',
  weekAverage: (avg: string) => `${avg} a day on average, last 7 days`,
  taperChartLabel: (start: string, limit: number | null, quit: string | null) =>
    `Your taper from ${start}${limit === null ? '' : `, starting at ${limit} a day`}${quit ? `, down to 0 on ${quit}` : ''}.`,
  daysOverLabel: (n: number) => `${n === 1 ? '1 day' : `${n} days`} went over the limit.`,
  quitDay: 'Quit day',
  legendLimit: 'Daily limit',
  legendSmoked: 'Smoked',
  legendOver: 'Over the limit',
  savedLine: (pesos: string) => `${pesos} saved against your usual`,
  overLine: (pesos: string) => `${pesos} more than your usual`,
  cigarettesToday: 'Cigarettes today',
  today: 'Today',
  logOne: 'Log one cigarette',
  editTotal: "Edit today's total",
  todaysTotal: "Today's total",
  totalFor: (label: string) => `Total for ${label}`,
  totalHint: 'This replaces the count for the day. Taps after it add on top.',
  cigarettes: 'Cigarettes',
  oneLess: 'One less',
  oneMore: 'One more',
  saveTotal: 'Save total',
  setTotal: 'Set the total',

  craving: "I'm having a craving",
  cravingTitle: 'A craving',
  cravingHint: 'It usually passes in a few minutes.',
  whatTriggered: 'What set it off? (optional)',
  wait10: 'Wait 10 minutes',
  delayLeft: (mmss: string) => `${mmss} left`,
  delayDone: 'Ten minutes. How are you doing?',
  gotThrough: 'I got through it',
  smokedOne: 'I smoked',
  cravingPassed: (n: number) => `Craving passed. ${n} resisted this week.`,

  needPlan: 'Set up your plan first. It takes a minute.',
  setUpPlan: 'Set up a plan',
  empty:
    'Set up a plan to start. Say how many you usually smoke, and Tahi suggests a gentle taper.',
  lastOne: (t: string) => `Last one ${t} ago`,
  noneYet: 'None logged yet',
  smokeFreeFor: (t: string) => `Smoke-free for ${t}`,
  quitDate: (d: string) => `Quit date ${d}`,
  cuttingTo: (n: number) => `Cutting down to ${n} a day`,
  nextStep: (date: string, limit: number) => `Next step ${date}: ${limit} a day`,

  money: 'Money',
  spentToday: 'Spent today',
  thisWeek: 'This week',
  allTime: 'All time',
  saved: (pesos: string) => `${pesos} saved`,
  overUsual: (pesos: string) => `${pesos} more than your usual`,
  savedSince: 'Since you started, against your usual.',
  noPrice: 'Add a price to see money spent and saved.',
  moneySavedTitle: 'Money saved',

  week: 'Last 7 days',
  history: 'History',
  triggers: 'Triggers',
  editPlan: 'Edit plan',
  pages: 'Quit Smoking pages',
  menuAverage: (avg: string) => `${avg} a day`,
  menuResisted: (n: number) => `${n} resisted this week`,
  undo: 'Undo',
  removed: (n: number) => `Removed. ${n} today.`,
  restored: 'Restored.',
  deleted: 'Deleted.',
  showOnToday: 'Show on Today',
  showOnTodayHint: 'Turn off to keep Quit Smoking off your Today screen. Nothing is deleted.',

  streakTitle: 'Progress',
  daysUnder: (n: number) => `${plural(n, 'day', 'days')} under your limit`,
  bestRun: (n: number) => `Best run ${plural(n, 'day', 'days')}`,
  smokeFreeDays: (n: number) => `${plural(n, 'smoke-free day', 'smoke-free days')}`,
  sinceQuit: (n: number) => `of ${plural(n, 'day', 'days')} since your quit date`,
  startsTomorrow: 'Counts from tomorrow.',

  milestones: 'Milestones',
  milestonesNote: 'General information from public health sources, not medical advice.',
  nextMilestone: (label: string, inText: string) => `Next: ${label}, in ${inText}`,
  reached: 'Reached',

  support: 'Support',
  country: 'Country',
  call: (name: string, phone: string) => `Call ${name}: ${phone}`,
  website: 'Website',
  nrtNote:
    'Nicotine replacement, like patches or gum, can ease cravings. A doctor or pharmacist can say what fits you. This is information, not medical advice.',

  wizardNew: 'Set up your plan',
  wizardEdit: 'Edit your plan',
  stepBaseline: 'How many do you usually smoke a day?',
  baseline: 'Cigarettes a day',
  stepPrice: 'What do you pay?',
  priceMode: 'Price',
  perPackMode: 'Per pack',
  perStickMode: 'Per stick',
  noPriceMode: "Don't track",
  packPrice: 'Pack price (₱)',
  perPack: 'Cigarettes per pack',
  stickPrice: 'Price per stick (₱)',
  stepSchedule: 'Your taper',
  scheduleHint: 'A suggestion. Change anything.',
  startLimit: "Today's limit",
  step: 'Lower by',
  everyDays: 'Every (days)',
  floor: 'Stop at',
  fund: 'Take the cost from',
  fundDefault: 'Default limit',
  next: 'Next',
  back: 'Back',
  savePlan: 'Save plan',
  planSaved: 'Plan saved.',
  preview: 'Preview',

  historyTitle: 'History',
  backfill: 'Fill in a past day',
  day: 'Day',
  open: 'Open',
  tapAt: (time: string) => `+1 at ${time}`,
  totalAt: (n: number, time: string) => `Set to ${n} at ${time}`,
  replaced: 'Replaced by a later total',
  deleteLog: (what: string) => `Delete ${what}`,
  noLogs: 'Nothing logged this day.',

  triggersTitle: 'Triggers',
  last30: 'Last 30 days',
  triggersEmpty: 'No triggers tagged yet. Tag one when you log a craving.',
  triggerStats: (smoked: number, resisted: number) => `Smoked ${smoked} · Got through ${resisted}`,
  yourPlan: 'Your plan',
  editTriggerPlan: (label: string) => `Edit plan for ${label}`,
  save: 'Save',
  cancel: 'Cancel',

  errors: {
    bad_baseline: 'Enter a number from 1 to 100.',
    bad_limit: 'Enter a limit from 0 to 100.',
    bad_step: 'Enter a step from 0 to 20.',
    bad_every_days: 'Enter a number of days from 1 to 60.',
    bad_floor: "Stop at can't be above today's limit.",
    bad_price: 'Enter a price above zero.',
    bad_count: 'Enter a number from 0 to 200.',
    bad_date: 'Pick a day.',
    future_date: "That day hasn't happened yet.",
    bad_trigger: 'Pick a trigger from the list.',
    no_plan: 'Set up your plan first. It takes a minute.',
    not_found: "That entry isn't there anymore.",
    too_long: 'Keep it under 200 characters.',
  } as Record<string, string>,
  genericError: "That didn't save. Try again.",
};

/** v0.7.5 back targets for the sub-screens' ‹ (the logical parent). */
export const QUIT_BACK = { to: '/m/quit_smoking', label: copy.moduleName };
export const HISTORY_BACK = { to: '/m/quit_smoking/history', label: copy.historyTitle };

export const errorText = (code: string) => copy.errors[code] ?? copy.genericError;
